/* SENFINECO Shell V2 — small accessibility and overlay-state enhancements. */
(function () {
  'use strict';

  var drawerSelector = '.sf-native-nav__details';
  var searchSelector = '.shopify-section-group-header-group predictive-search';
  var initializedDrawers = new WeakSet();
  var initializedSearches = new WeakSet();

  function syncIphoneKeyboardState() {
    if (!window.matchMedia('(max-width: 1024px)').matches) {
      document.body.classList.remove('sf-iphone-keyboard-open');
      return;
    }
    var active = document.activeElement;
    var editable = active && active.matches && active.matches('input, textarea, [contenteditable="true"]');
    var viewport = window.visualViewport;
    var obscured = viewport ? window.innerHeight - viewport.height - viewport.offsetTop : 0;
    document.body.classList.toggle('sf-iphone-keyboard-open', Boolean(editable && obscured > 120));
  }

  function normalizePath(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      var path = parsed.pathname.replace(/\/+$/, '') || '/';
      return path;
    } catch (error) {
      return '';
    }
  }

  function getFocusable(container) {
    if (!container) return [];

    return Array.prototype.filter.call(
      container.querySelectorAll(
        'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (element) {
        return !element.hidden && element.getClientRects().length > 0;
      }
    );
  }

  function setPageInert(shouldInert) {
    ['#MainContent', '.footer-group'].forEach(function (selector) {
      var element = document.querySelector(selector);
      if (!element || !('inert' in element)) return;

      if (shouldInert) {
        if (!element.inert) {
          element.inert = true;
          element.setAttribute('data-sfv2-inert', '');
        }
      } else if (element.hasAttribute('data-sfv2-inert')) {
        element.inert = false;
        element.removeAttribute('data-sfv2-inert');
      }
    });
  }

  function syncOverlayInert() {
    var navigationOpen = Boolean(document.querySelector(drawerSelector + '[open]'));
    var searchOpen = Boolean(document.querySelector(searchSelector + '[open]'));
    setPageInert(navigationOpen || searchOpen);
  }

  function syncDrawerState() {
    var openDrawer = document.querySelector(drawerSelector + '[open]');
    document.body.classList.toggle('sf-shell-nav-open', Boolean(openDrawer));
    syncOverlayInert();
  }

  function syncHeaderMetrics() {
    if (!window.matchMedia('(max-width: 1024px)').matches) {
      document.documentElement.style.removeProperty('--sfv2-header-bottom');
      return;
    }

    var header = document.querySelector('.shopify-section-group-header-group');
    if (!header) return;

    var rect = header.getBoundingClientRect();
    var bottom = Math.max(0, Math.min(window.innerHeight, Math.round(rect.bottom)));
    document.documentElement.style.setProperty('--sfv2-header-bottom', bottom + 'px');
  }

  function closeDrawer(drawer, returnFocus) {
    if (!drawer || !drawer.open) return;
    drawer.open = false;
    syncDrawerState();

    if (returnFocus) {
      var trigger = drawer.querySelector(':scope > summary');
      if (trigger) trigger.focus();
    }
  }

  function markCurrentLinks(drawer) {
    var currentPath = normalizePath(window.location.href);

    drawer.querySelectorAll('a[href]').forEach(function (link) {
      var linkPath = normalizePath(link.href);
      if (linkPath && linkPath === currentPath) {
        link.setAttribute('aria-current', 'page');
      }
    });
  }

  function syncDrawerScrollbar(drawer) {
    if (!drawer) return;

    var scroll = drawer.querySelector('[data-sf-native-nav-scroll]');
    var scrollbar = drawer.querySelector('[data-sf-native-nav-scrollbar]');
    var thumb = drawer.querySelector('[data-sf-native-nav-scroll-thumb]');
    if (!scroll || !scrollbar || !thumb) return;

    var viewportHeight = scroll.clientHeight;
    var contentHeight = scroll.scrollHeight;
    var trackHeight = scrollbar.clientHeight;
    var canScroll = contentHeight > viewportHeight + 2 && trackHeight > 0;

    scrollbar.classList.toggle('is-hidden', !canScroll);
    if (!canScroll) return;

    var thumbHeight = Math.max(54, Math.round(trackHeight * (viewportHeight / contentHeight)));
    var maxThumbOffset = Math.max(0, trackHeight - thumbHeight);
    var maxScrollOffset = Math.max(1, contentHeight - viewportHeight);
    var thumbOffset = Math.round(maxThumbOffset * (scroll.scrollTop / maxScrollOffset));

    scrollbar.style.setProperty('--sf-nav-thumb-height', thumbHeight + 'px');
    scrollbar.style.setProperty('--sf-nav-thumb-offset', thumbOffset + 'px');
  }

  function initDrawer(drawer) {
    if (!drawer || initializedDrawers.has(drawer)) return;
    initializedDrawers.add(drawer);

    var trigger = drawer.querySelector(':scope > summary');
    var groups = drawer.querySelectorAll('.sf-native-nav__group');
    var scroll = drawer.querySelector('[data-sf-native-nav-scroll]');

    if (trigger) {
      trigger.setAttribute('aria-expanded', drawer.open ? 'true' : 'false');
    }

    markCurrentLinks(drawer);

    drawer.addEventListener('toggle', function () {
      if (trigger) trigger.setAttribute('aria-expanded', drawer.open ? 'true' : 'false');

      if (drawer.open) {
        document.querySelectorAll(drawerSelector + '[open]').forEach(function (other) {
          if (other !== drawer) other.open = false;
        });
        window.requestAnimationFrame(function () {
          syncDrawerScrollbar(drawer);
        });
      }

      syncDrawerState();
    });

    drawer.addEventListener('click', function (event) {
      if (drawer.open && event.target === drawer) {
        closeDrawer(drawer, true);
        return;
      }

      var link = event.target.closest('a[href]');
      if (link && drawer.contains(link)) closeDrawer(drawer, false);
    });

    groups.forEach(function (group) {
      group.addEventListener('toggle', function () {
        if (group.open) {
          groups.forEach(function (other) {
            if (other !== group) other.open = false;
          });
        }

        window.requestAnimationFrame(function () {
          syncDrawerScrollbar(drawer);
        });
      });
    });

    if (scroll) {
      scroll.addEventListener('scroll', function () {
        syncDrawerScrollbar(drawer);
      }, { passive: true });

      if ('ResizeObserver' in window) {
        var drawerResizeObserver = new ResizeObserver(function () {
          syncDrawerScrollbar(drawer);
        });
        drawerResizeObserver.observe(scroll);
        var scrollContent = scroll.querySelector('.sf-native-nav__scroll-content');
        if (scrollContent) drawerResizeObserver.observe(scrollContent);
      }
    }

    drawer.addEventListener('keydown', function (event) {
      if (!drawer.open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer(drawer, true);
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = getFocusable(drawer);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function syncSearchState() {
    var searchOpen = Boolean(document.querySelector(searchSelector + '[open]'));
    document.body.classList.toggle('sf-shell-search-open', searchOpen);
    syncOverlayInert();
  }

  function initSearch(search) {
    if (!search || initializedSearches.has(search)) return;
    initializedSearches.add(search);

    var toggle = search.querySelector('[data-predictive-search-toggle]');
    var mobileModalTop = search.querySelector('.predictive-search--mobile .predictive-search__modal-top');
    if (toggle) {
      if (!toggle.hasAttribute('role')) toggle.setAttribute('role', 'button');
      if (!toggle.hasAttribute('aria-label')) {
        var isArabic = document.documentElement.lang.toLowerCase().indexOf('ar') === 0;
        toggle.setAttribute('aria-label', isArabic ? 'فتح البحث' : 'Open search');
      }
      toggle.setAttribute('aria-expanded', search.hasAttribute('open') ? 'true' : 'false');

      toggle.addEventListener('keydown', function (event) {
        if (event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        toggle.click();
      });
    }

    if (mobileModalTop && !mobileModalTop.querySelector('[data-sfv2-search-close]')) {
      var isArabic = document.documentElement.lang.toLowerCase().indexOf('ar') === 0;
      var closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'sfv2-search-close';
      closeButton.setAttribute('data-sfv2-search-close', '');
      closeButton.setAttribute('aria-label', isArabic ? 'إغلاق البحث' : 'Close search');
      closeButton.innerHTML = '<span aria-hidden="true">&times;</span>';
      closeButton.addEventListener('click', function () {
        if (typeof search.close === 'function') {
          search.close(false, true);
        } else {
          search.removeAttribute('open');
        }
        if (toggle) toggle.focus();
      });
      mobileModalTop.appendChild(closeButton);
    }

    var observer = new MutationObserver(function () {
      if (toggle) {
        toggle.setAttribute('aria-expanded', search.hasAttribute('open') ? 'true' : 'false');
      }
      syncSearchState();
    });

    observer.observe(search, { attributes: true, attributeFilter: ['open'] });
  }

  function initialize(root) {
    var scope = root && root.querySelectorAll ? root : document;

    if (scope.matches && scope.matches(drawerSelector)) initDrawer(scope);
    scope.querySelectorAll(drawerSelector).forEach(initDrawer);

    if (scope.matches && scope.matches(searchSelector)) initSearch(scope);
    scope.querySelectorAll(searchSelector).forEach(initSearch);

    syncDrawerState();
    syncSearchState();
    syncHeaderMetrics();
  }

  function onReady() {
    initialize(document);
    syncIphoneKeyboardState();

    document.addEventListener('shopify:section:load', function (event) {
      initialize(event.target);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var drawer = document.querySelector(drawerSelector + '[open]');
      if (drawer) closeDrawer(drawer, true);
    });

    window.addEventListener('pageshow', function () {
      initialize(document);
    });

    window.addEventListener('resize', syncHeaderMetrics, { passive: true });
    window.addEventListener('orientationchange', syncHeaderMetrics, { passive: true });
    document.addEventListener('focusin', syncIphoneKeyboardState, { passive: true });
    document.addEventListener('focusout', function () {
      window.setTimeout(syncIphoneKeyboardState, 80);
    }, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncIphoneKeyboardState, { passive: true });
      window.visualViewport.addEventListener('scroll', syncIphoneKeyboardState, { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }
})();
